import React, { Children, memo } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useScreenInit } from '../useScreenInit';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PRODUCT_NAME } from '../constants/site';
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-10" data-id="element-205" data-mp="3k8830meih1ou">
      <h2 className="text-xl font-bold tracking-tight text-slate-900" data-id="element-206" data-mp="3k7eupgzwm44l">
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600" data-id="element-207" data-mp="3f9wv4h7s1199">
        {children}
      </div>
    </section>;
}
const PERMS = [{
  name: 'activeTab',
  why: 'Capture the visible area of the X.com tab only after you click the toolbar icon.'
}, {
  name: 'scripting',
  why: 'Inject the small piece of code that scrolls the post into view while stitching long captures.'
}, {
  name: 'downloads',
  why: 'Let you save the resulting PNG to your local disk via the save dialog.'
}, {
  name: 'clipboardWrite',
  why: 'Let you copy the resulting PNG to the system clipboard.'
}, {
  name: 'Content script on x.com / twitter.com',
  why: 'Render the picker UI and result dialog on those sites.'
}];
export function Privacy() {
  useScreenInit();
  return <div className="flex min-h-screen w-full flex-col bg-white" data-id="element-208" data-mp="uvf6zrnkr8ko">
      <Navbar data-id="element-209" data-mp="zrvuqkqv3kx8" />
      <main className="flex-1 bg-slate-50" data-id="element-210" data-mp="zse14a78nfiv">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-id="element-211" data-mp="14r8si0j9ucel">
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900" data-id="element-212" data-mp="14tuxbses2dt4">
            <ArrowBackIcon sx={{
            fontSize: 18
          }} data-id="element-213" data-mp="1drdpdgqu9sei" />{' '}
            Back to home
          </Link>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10" data-id="element-214" data-mp="3fjtgw1j3jgko">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-id="element-215" data-mp="3flmphmd9savb">
              {PRODUCT_NAME} Privacy Policy
            </h1>
            <p className="mt-2 text-sm text-slate-400" data-id="element-216" data-mp="hr01ulg37d83">
              Last updated: May 23, 2026
            </p>

            <p className="mt-6 text-sm leading-relaxed text-slate-600" data-id="element-217" data-mp="vqssyhvizvgl">
              {PRODUCT_NAME} is a Chrome browser extension that lets you capture
              screenshots of individual posts on X.com (formerly Twitter).
            </p>

            <div className="mt-6 rounded-xl border border-[#1d9bf0]/30 bg-[#1d9bf0]/5 p-4" data-id="element-218" data-mp="15luk1j9d37lt">
              <p className="text-sm font-semibold text-slate-900" data-id="element-219" data-mp="10n9qg2om7m6c">TL;DR</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-700" data-id="element-220" data-mp="1efzaw2ohwj1c">
                {PRODUCT_NAME} does not collect, store, transmit, or share any
                personal data. All screenshots are created and remain on your
                own device. The extension never contacts any server controlled
                by the developer.
              </p>
            </div>

            <Section title="Data the extension handles" data-id="element-221" data-mp="232oz65zdy4jb">
              <p data-id="element-222" data-mp="280gsn4fohypm">
                When you click the {PRODUCT_NAME} toolbar icon and then pick a
                post:
              </p>
              <ol className="list-decimal space-y-2 pl-5" data-id="element-223" data-mp="1t269gul53tth">
                <li data-id="element-224" data-mp="1y2asar9it0mv">
                  The extension captures the visible portion of the current
                  X.com / twitter.com tab and stitches together a PNG image of
                  the chosen post.
                </li>
                <li data-id="element-225" data-mp="1p2nyaqj4ex3u">
                  The resulting image is shown to you in a dialog on the same
                  page.
                </li>
                <li data-id="element-226" data-mp="1aaltcs271dwy">
                  You may <strong data-id="element-227" data-mp="1fbji5nhpl67f">Download</strong> the image to your local disk
                  or <strong data-id="element-228" data-mp="1f9gjkf2hq4r3">Copy</strong> it to your system clipboard. Both
                  operations are local to your device.
                </li>
              </ol>
              <p data-id="element-229" data-mp="15d3rtmqia0yz">
                The image is held in memory only for as long as the result
                dialog is open and is discarded when you close it.
              </p>
            </Section>

            <Section title="What the extension does NOT do" data-id="element-230" data-mp="tbud8hd00jkn">
              <ul className="list-disc space-y-2 pl-5" data-id="element-231" data-mp="ok3w9sjk2xk9">
                <li data-id="element-232" data-mp="oij3o8gsxop1">
                  It does <strong data-id="element-233" data-mp="jlsmtix6ap2k">not</strong> upload, send, or transmit any
                  image, URL, post content, account information, or browsing
                  data to the developer or any third party.
                </li>
                <li data-id="element-234" data-mp="akkz1usa96mq">
                  It does <strong data-id="element-235" data-mp="ajje8ylgt5rt">not</strong> include any analytics, telemetry,
                  advertising, tracking pixels, or fingerprinting.
                </li>
                <li data-id="element-236" data-mp="ngdnx69hf6z">
                  It does <strong data-id="element-237" data-mp="o7yccyblsih">not</strong> use cookies or any remote
                  storage.
                </li>
                <li data-id="element-238" data-mp="1zyzmmbrkengn">
                  It does <strong data-id="element-239" data-mp="1v0oblxpja6wu">not</strong> read or store the content of any
                  web page outside of the single screenshot you explicitly
                  capture.
                </li>
              </ul>
            </Section>

            <Section title="Permissions and why they are required" data-id="element-240" data-mp="27iiez8mp7t1w">
              <div className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200" data-id="element-241" data-mp="2ca0gbxsmvvne">
                {PERMS.map(p => <div key={p.name} className="flex flex-col gap-1 bg-white p-4 sm:flex-row sm:gap-4" data-id="element-242" data-mp="2h7s6ondmr9ph">
                    <code className="w-fit shrink-0 rounded-md bg-[#1d9bf0]/10 px-2 py-0.5 text-xs font-semibold text-[#1d9bf0]" data-id="element-243" data-mp="1ojw9lhk5ffs6">
                      {p.name}
                    </code>
                    <span className="text-sm text-slate-600" data-id="element-244" data-mp="1yic29oytqx3s">{p.why}</span>
                  </div>)}
              </div>
              <p data-id="element-245" data-mp="34xmhz0iawq7m">
                The extension does <strong data-id="element-246" data-mp="350s4c7zjpdzn">not</strong> request{' '}
                <code className="rounded bg-slate-100 px-1 py-0.5 text-xs" data-id="element-247" data-mp="39wzcm7fez1kr">
                  &lt;all_urls&gt;
                </code>{' '}
                or access to any other websites.
              </p>
            </Section>

            <Section title="Children's privacy" data-id="element-248" data-mp="2fa0itfa5zq5r">
              <p data-id="element-249" data-mp="2u7j427nmvl8g">
                {PRODUCT_NAME} does not knowingly collect any information from
                anyone, including children under 13.
              </p>
            </Section>

            <Section title="Changes to this policy" data-id="element-250" data-mp="21j57lwc70wz0">
              <p data-id="element-251" data-mp="21jnbnf53kfhh">
                If this policy changes in a way that affects what data is
                handled, the update will be reflected in a new release of the
                extension and the “Last updated” date above.
              </p>
            </Section>

            <Section title="Contact" data-id="element-252" data-mp="26hykpnnkilyn">
              <p data-id="element-253" data-mp="26gx8vgyq9vfv">
                For questions about this policy or the extension, please open an
                issue on the project's source repository.
              </p>
            </Section>
          </div>
        </div>
      </main>
      <Footer data-id="element-254" data-mp="3pgwfw4xlddmx" />
    </div>;
}